import { Stack, styled, Typography } from '@mui/material';
import { DescriptionListWrapper } from '@side/pantry';
import type { ReferralSummary } from '@/common/hooks/referrals/types';
import { ViewDetailsButton } from '@/components/ViewDetailsButton';
import { TXM_REFERRALS_PATH } from '@/constants';
import { ReferralDirectionArrow } from '@/views/Referrals/components/ReferralDirectionArrow';
import {
  TrackReferralsEvent,
  useTrackReferralsAnalytics,
} from '$analytics/useTrackAnalytics/domains/referrals';
import { TransactionStatus } from '../../components/TransactionStatus';
import { AgreementStatus } from './AgreementStatus';
import { ReferralTableActionsMenu } from './components/ReferralTableActionsMenu';
import { ReferralDate } from './ReferralDate';

type ReferralCardProps = {
  isArchived?: boolean;
  referral: ReferralSummary;
};

const DD = styled('dd')(({ theme }) => ({
  paddingLeft: theme.spacing(5.5),
}));

export function ReferralCard({ isArchived, referral }: ReferralCardProps) {
  const {
    agreementStatus,
    client,
    endDate,
    id,
    referringAgent,
    transactingAgent,
    transactionStatus,
  } = referral;

  const trackEvent = useTrackReferralsAnalytics();

  const bothSideAgents = Boolean(
    referringAgent?.isSideAgent && transactingAgent?.isSideAgent,
  );

  return (
    <DescriptionListWrapper
      Button={
        <ViewDetailsButton
          href={`${TXM_REFERRALS_PATH}/${id}`}
          onClick={() => {
            trackEvent(TrackReferralsEvent.ClickViewReferralOnListPage, {
              eventTarget: 'referral_details_button',
              interactionId: id,
            });
          }}
        />
      }
      title={
        <Stack
          sx={{
            alignItems: 'center',
            flexDirection: 'row',
            gap: 1,
          }}
        >
          <Typography component="span" variant="subtitle2" noWrap>
            {referringAgent?.displayName}
          </Typography>

          <ReferralDirectionArrow
            color={bothSideAgents ? 'secondary' : 'default'}
          />

          <Typography component="span" variant="subtitle2" noWrap>
            {transactingAgent?.displayName}
          </Typography>
        </Stack>
      }
      TitleAdornment={
        <ReferralTableActionsMenu referralId={id} isArchived={isArchived} />
      }
      TitleProps={{
        noWrap: true,
      }}
    >
      <dt>
        <Typography variant="body2">End Date</Typography>
      </dt>
      <DD>
        <ReferralDate date={endDate} includeDistance />
      </DD>

      <dt>
        <Typography variant="body2">Client</Typography>
      </dt>
      <DD>
        <Typography sx={{ textTransform: 'capitalize' }} variant="body2">
          {client?.displayName}
          {client && client.clientTypesList.length > 0 && (
            <> ({client.clientTypesList.join(', ')})</>
          )}
        </Typography>
      </DD>

      <dt>
        <Typography variant="body2">Agreement</Typography>
      </dt>
      <DD>
        <AgreementStatus
          referralId={id}
          agreementStatus={agreementStatus}
          transactionStatus={transactionStatus}
        />
      </DD>

      <dt>
        <Typography variant="body2">Transaction</Typography>
      </dt>
      <DD>
        <TransactionStatus referral={referral} />
      </DD>
    </DescriptionListWrapper>
  );
}
